import type { TranscriptEntry } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronRight, Loader2, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { SessionLink } from '@/components/session-link';
import { Entry } from '@/components/work-item/agent-session';
import { api } from '@/lib/api';
import { useNow } from '@/lib/hooks';
import { intlLocale, t, tc } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useRealtimeStatus } from '@/lib/realtime';
import { friendlyStep } from '@/lib/step-text';
import { useAutoScroll } from '@/lib/use-auto-scroll';
import { cn } from '@/lib/utils';

/** Open or closed is the person's choice, remembered for every panel (a per-viewer convenience). */
const OPEN_KEY = 'deskworks.agentSteps.open';

/** Which turn of a chat the panel shows; one run (execution) answers several messages in a row. */
export interface Turn {
  /** Agent replies this run has given so far (messages from the agent since the execution was created). */
  replies: number;
  /** The last message is the person's: the agent is on the next turn. */
  open: boolean;
}

function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * The steps of one turn. Every turn starts with the runtime handing the model the message (`user`, or the `prompt`
 * entries of the first turn — consecutive ones count as one start), so the k-th start begins the turn that gives the
 * k-th reply. Counting needs no clock: the worker's step times and Deskworks's message times are never compared.
 */
export function turnEntries(entries: TranscriptEntry[], turn: Turn | null | undefined): TranscriptEntry[] {
  if (!turn) return entries;
  const starts: number[] = []; // index of the first step after each start group
  // A message that reached the agent during its turn (`data.during`: between its steps, or a reply held back for it)
  // is a step of that turn, not the start of the next.
  const isStart = (e: TranscriptEntry) => (e.kind === 'user' || e.kind === 'prompt') && !e.data?.during;
  entries.forEach((e, i) => {
    if (isStart(e) && !(i > 0 && isStart(entries[i - 1]))) starts.push(i);
  });
  const index = turn.open ? turn.replies : turn.replies - 1;
  if (index < 0 || index >= starts.length) return [];
  const from = starts[index];
  const to = starts[index + 1] ?? entries.length;
  // The person's message is already in the chat; prompts are the runtime's instructions, not a step.
  return entries.slice(from, to).filter((e) => e.kind !== 'prompt' && e.kind !== 'user');
}

/** The current turn of a chat, for `AgentSteps`: replies the run gave so far, and whether the person spoke last. */
export function currentTurn(messages: { senderType: string; createdAt: string }[], executionCreatedAt: string): Turn {
  const since = new Date(executionCreatedAt).getTime();
  const replies = messages.filter((m) => m.senderType === 'agent' && new Date(m.createdAt).getTime() >= since).length;
  const last = messages[messages.length - 1];
  return { replies, open: last?.senderType === 'user' };
}

/** Arguments that tell one call of a tool from another, the first one present wins. */
const DETAIL_KEYS = ['title', 'query', 'pattern', 'url', 'file_path', 'path', 'name', 'description', 'question', 'message'];

function oneLine(text: string, max = 80): string {
  const line = text.trim().split('\n')[0];
  return line.length > max ? `${line.slice(0, max)}…` : line;
}

/** What a tool call is about ("Betriebsstatus mit Cluster-Zugriff", "src/app.ts"), from its arguments. */
function toolDetail(e: TranscriptEntry): string | null {
  let args = e.data.arguments;
  if (typeof args === 'string') {
    try {
      args = JSON.parse(args);
    } catch {
      return null;
    }
  }
  if (!args || typeof args !== 'object') return null;
  for (const key of DETAIL_KEYS) {
    const value = (args as Record<string, unknown>)[key];
    if (typeof value === 'string' && value.trim()) return oneLine(value);
  }
  return null;
}

/**
 * One line that says what the agent does in this step: as it happens (`now`, the live step) or as done (the steps
 * before it, and the summary of a finished turn). Commands and tool calls follow their own status.
 */
export function stepLabel(e: TranscriptEntry, now = true): string {
  const running = e.data.status === 'running';
  const friendly = friendlyStep(e, now && running);
  if (friendly) return friendly;
  switch (e.kind) {
    case 'command': {
      const what = typeof e.data.description === 'string' && e.data.description ? e.data.description : `$ ${e.title ?? t('command')}`;
      return running ? t('Running: {what}', { what }) : t('Ran: {what}', { what });
    }
    case 'tool_call': {
      const tool = e.title ?? t('a tool');
      const detail = toolDetail(e);
      if (detail) return running ? t('Using {tool}: {detail}', { tool, detail }) : t('Used {tool}: {detail}', { tool, detail });
      return running ? t('Using {tool}', { tool }) : t('Used {tool}', { tool });
    }
    case 'reasoning':
      return now ? t('Thinking') : t('Thought it over');
    case 'message':
      return now ? t('Writing') : t('Wrote');
    case 'file_change':
      return now ? t('Changing files') : tc('past', 'Changed files');
    case 'web_search':
      if (now) return e.content ? t('Searching the web: {query}', { query: e.content }) : t('Searching the web');
      return e.content ? t('Searched the web: {query}', { query: e.content }) : t('Searched the web');
    case 'todo':
      return now ? t('Planning') : t('Planned');
    case 'error':
      return t('Error: {error}', { error: e.title ?? e.content ?? '' });
    case 'user':
      return now ? t('Reading your message') : t('Read a new message');
    default:
      return e.title ?? e.kind;
  }
}

/** What a finished turn did, in one line: its actions; thinking and writing the reply only when that was all. */
function turnSummary(entries: TranscriptEntry[]): string {
  const actions = entries.filter((e) => e.kind !== 'reasoning' && e.kind !== 'message').map((e) => stepLabel(e, false));
  if (actions.length === 0) return t('thought it over and replied');
  return actions.filter((label, i) => label !== actions[i - 1]).join(' · ');
}

/** Steps shown while the agent works, the current one included. */
const LIVE_STEPS = 3;

/**
 * While the agent works, its last steps and what it does right now stay in view, also with the panel closed. Between
 * steps the model is thinking (the runtime reports a step only once it starts or ends); the seconds since the last
 * step show that it still is. Counted from when this page saw the step: the worker's clock is never compared.
 */
function LiveSteps({ entries }: { entries: TranscriptEntry[] }) {
  const last = entries[entries.length - 1];
  const busy = last?.data.status === 'running';
  const key = `${entries.length}:${last?.data.status ?? ''}`;
  const [since, setSince] = useState(() => ({ key, at: Date.now() }));
  if (since.key !== key) setSince({ key, at: Date.now() });
  const now = useNow(1000);
  const seconds = Math.max(0, Math.floor((now - since.at) / 1000));
  const done = (busy ? entries.slice(0, -1) : entries).slice(-(LIVE_STEPS - 1));
  const current = busy ? stepLabel(last) : t('Thinking');
  return (
    <ol className="flex flex-col gap-0.5 px-2.5 pb-2" aria-live="polite">
      {done.map((e) => (
        <li key={`${e.id}-${e.data.status ?? ''}`} className="flex items-center gap-2 text-muted-foreground">
          <Check className="size-3.5 shrink-0" />
          <span className="min-w-0 truncate" title={stepLabel(e, false)}>
            {stepLabel(e, false)}
          </span>
        </li>
      ))}
      <li className="flex items-center gap-2 font-medium text-foreground">
        {busy ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-indigo-600 dark:text-indigo-300" />
        ) : (
          <Sparkles className="size-3.5 shrink-0 animate-soft-pulse text-indigo-600 dark:text-indigo-300" />
        )}
        <span className="min-w-0 truncate" title={current}>
          {current}
        </span>
        {seconds >= 3 && <span className="shrink-0 font-normal text-muted-foreground tabular-nums">{t('{n} s', { n: seconds })}</span>}
      </li>
    </ol>
  );
}

/**
 * What an agent is doing, live, as a collapsed row — one click opens the steps (docs/features/inbox-activity-sessions.md).
 * The same session the Sessions page shows afterwards; nothing is recorded twice. New steps arrive through the
 * `session.transcript` / `work_item.transcript` realtime events (they mark the session query stale); while the socket is
 * down a working agent is polled.
 */
export function AgentSteps({
  executionId,
  turn,
  working,
  agentName,
  defaultOpen,
  className,
}: {
  executionId: string | null | undefined;
  /** When set (chats): only the steps of this turn (see `currentTurn`). */
  turn?: Turn | null;
  /** The page's own knowledge of whether the agent is busy (drives the spinner and polling). */
  working: boolean;
  /** Named in the header; leave out where a line above already says who is working. */
  agentName?: string | null;
  /** Open from the start and not remembered (the training shows every step); otherwise the person's last choice. */
  defaultOpen?: boolean;
  className?: string;
}) {
  const realtime = useRealtimeStatus();
  const [open, setOpen] = useState(defaultOpen ?? readOpen);
  const session = useQuery({
    queryKey: qk.session(executionId ?? ''),
    queryFn: () => api.sessions.get(executionId!),
    enabled: !!executionId,
    refetchInterval: working && realtime !== 'connected' ? 3000 : false,
  });
  const entries = turnEntries(session.data?.entries ?? [], turn);
  const last = entries[entries.length - 1];
  const summary = !working && entries.length > 0 ? turnSummary(entries) : null;
  const scrollRef = useAutoScroll<HTMLOListElement>(`${entries.length}:${last?.data.status ?? ''}`);

  const toggle = () => {
    setOpen((v) => {
      if (defaultOpen !== undefined) return !v;
      try {
        localStorage.setItem(OPEN_KEY, v ? '0' : '1');
      } catch {
        // Remembering the choice is only a convenience.
      }
      return !v;
    });
  };
  if (!executionId || (!working && entries.length === 0)) return null;
  const count = entries.length === 1 ? t('1 step') : t('{n} steps', { n: entries.length.toLocaleString(intlLocale) });
  return (
    <div className={cn('rounded-md border bg-card/60 text-xs', className)}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-2 px-2.5 py-1.5 text-left text-muted-foreground hover:text-foreground"
      >
        <ChevronRight className={cn('size-3.5 shrink-0 transition-transform', open && 'rotate-90')} />
        {working && open && <Loader2 className="size-3.5 shrink-0 animate-spin text-indigo-600 dark:text-indigo-300" />}
        <span className="shrink-0 font-medium text-foreground">{working ? `${agentName ? t('{agent} is working', { agent: agentName }) : t('Working')} · ${count}` : count}</span>
        {summary && (
          <span className="min-w-0 flex-1 truncate" title={summary}>
            {summary}
          </span>
        )}
        {open && <SessionLink executionId={executionId} label={t('Open in Sessions')} className="ml-auto shrink-0" />}
      </button>
      {working && !open && <LiveSteps entries={entries} />}
      {open && (
        <ol ref={scrollRef} className="max-h-96 divide-y overflow-y-auto border-t">
          {entries.length === 0 ? (
            <li className="px-5 py-3 text-muted-foreground italic">{t('No steps yet.')}</li>
          ) : (
            entries.map((e) => <StepRow key={`${e.id}-${e.data.status ?? ''}`} entry={e} />)
          )}
        </ol>
      )}
    </div>
  );
}

/** One step in words; a click shows what exactly the agent ran and got back. */
function StepRow({ entry }: { entry: TranscriptEntry }) {
  const [open, setOpen] = useState(false);
  const running = entry.data.status === 'running';
  const label = stepLabel(entry, running);
  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-2 px-2.5 py-1.5 text-left hover:bg-muted/50"
      >
        {running ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-indigo-600 dark:text-indigo-300" />
        ) : (
          <Check className="size-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className={cn('min-w-0 flex-1', !open && 'truncate')}>{label}</span>
        <ChevronRight className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')} />
      </button>
      {open && (
        <ol className="border-t bg-muted/30">
          <Entry entry={entry} openAll />
        </ol>
      )}
    </li>
  );
}
